import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  CompanyDraft,
  DealAssociation,
  DealDetailsFormValues,
  LandholdingDraft,
  ProjectDraft,
  SelectData,
} from "../../../../models";
import { deleteProject } from "../../../../service/project";
// import { CompanyDraft, DealAssociation, LandholdingDraft, ProjectDraft, SelectData } from "../../../../models";
import { searchProjects, SearchProjectsRequest } from "../../../../service/publicquery";
import { searchCollaborators } from "../../../../service/query";
import { FilterGroup, FilterGroups, ServiceError, Status } from "../../../../service/Shared";
import { getDefaultInvestmentDirectoryProjectFilterGroups } from "../../../../utils/project/projectSearchFilters";
import { Toast } from "../../../../widget";
import { NewDealJourneyContext } from "../NewDealJourneyContext";

const LANDHOLDING_OPTION_PREFIX = "landholding:";
const getProjectSearchFilterGroups = (
  searchTerm: string
): FilterGroup<NonNullable<SearchProjectsRequest["filter"]>>[] => {
  const listingFilterGroups = getDefaultInvestmentDirectoryProjectFilterGroups();

  if (!searchTerm) {
    return listingFilterGroups;
  }

  return [
    {
      operator: "and",
      filterGroups: [
        ...listingFilterGroups,
        {
          operator: "or",
          filterGroups: [
            {
              operator: "and",
              filter: {
                results: {
                  displayName: {
                    operator: "startsWith",
                    value: searchTerm,
                  },
                },
              },
            },
            {
              operator: "and",
              filter: {
                results: {
                  landholdings: {
                    some: {
                      groupName: {
                        operator: "startsWith",
                        value: searchTerm,
                      },
                    },
                  },
                },
              } as never,
            },
          ],
        },
      ],
    },
  ];
};

interface UseDealAssociationsReturnData {
  errors: ServiceError[] | undefined;
  investingIntoSelectData: SelectData;
  companySelectData: SelectData;
  // Search results plus the pending draft, so the picker can display a company that has no uuid yet.
  companyPickerData: SelectData;
  // The deal's single company, from either a draft or an already-chosen collaborator.
  selectedCompany: { key: string; name: string } | null;
  projectsSelectData: SelectData;
  selectedInvestedInto: string | null;
  associations: DealAssociation[];
  companyDraft: CompanyDraft | null;
  projectDrafts: ProjectDraft[];
  landholdingDrafts: LandholdingDraft[];
  showAddEditCompanyModal: boolean;
  // The term typed into the picker when "Add new company" was chosen, used to prefill the form.
  pendingCompanyName: string;
  showProjectCreatePanel: boolean;
  openAddEditCompanyModal: (currentSearchTerm: string) => void;
  closeAddEditCompanyModal: () => void;
  openProjectCreatePanel: () => void;
  closeProjectCreatePanel: () => void;
  saveCompanyDraft: (draft: CompanyDraft) => void;
  saveProjects: (projects: ProjectDraft[], landholding: LandholdingDraft, isLandholding: boolean) => void;
  clearCompanyDraft: () => void;
  removeLandholdingDraft: (draftKey: string) => void;
  removeProjectDraft: (draftKey: string) => Promise<void>;
  onCompanySelect: (value: string) => void;
  onInvestedIntoSelect: (value: string) => void;
  onProjectSelect: (value: string) => void;
  onCompanyTypeSearch: (value: string) => void;
  onProjectTypeSearch: (value: string) => void;
  removeAssociation: (value: DealAssociation) => void;
}

const investingIntoSelectData = [
  { key: "Company", value: "Company" },
  { key: "Project(s)", value: "Project(s)" },
  { key: "Company + Project(s)", value: "Company + Project(s)" },
];

const collaboratorKeys = (formValues: DealDetailsFormValues): string[] => [
  ...formValues.associations
    .filter((association) => association.objectType === "Collaborator")
    .map((association) => association.objectUuid),
  ...(formValues.companyDraft ? [formValues.companyDraft.key] : []),
];

/*
  Swaps the deal's single company (BR: one associated company per deal), whether the incoming one is a
  collaborator picked from the CRM or a draft that does not exist yet. Replacing rather than appending is what
  keeps the rule now that the picker stays editable instead of locking once a company is set.

  structureInvestments holds a bare copy of the association key with no type alongside it, and nothing prunes it
  outside the Structure page's own effect — so the outgoing key is remapped here, in the same update. Remapped
  rather than dropped: an investment tied to "the company" should follow the swap, not silently lose it.
*/
const replaceCompany = (
  formValues: DealDetailsFormValues,
  next: { association: DealAssociation } | { draft: CompanyDraft }
): DealDetailsFormValues => {
  const outgoing = new Set(collaboratorKeys(formValues));
  const incomingKey = "draft" in next ? next.draft.key : next.association.objectUuid;
  const createdCompanyDrafts = formValues.createdCompanyDrafts ?? [];

  return {
    ...formValues,
    companyDraft: "draft" in next ? next.draft : null,
    /*
      A company created here outlives being deselected, so switching away and back does not mean filling the
      form in again. Only companyDraft is ever created against the deal.
    */
    createdCompanyDrafts:
      "draft" in next && !createdCompanyDrafts.some((created) => created.key === next.draft.key)
        ? [...createdCompanyDrafts, next.draft]
        : createdCompanyDrafts,
    associations: [
      ...formValues.associations.filter((association) => association.objectType !== "Collaborator"),
      ...("draft" in next ? [] : [next.association]),
    ],
    structureInvestments: (formValues.structureInvestments ?? []).map((investment) => ({
      ...investment,
      associations: Array.from(new Set(investment.associations.map((key) => (outgoing.has(key) ? incomingKey : key)))),
    })),
  };
};

export const useDealAssociations = (): UseDealAssociationsReturnData => {
  const { errors, formValues, setFormValues, handleFieldChange } = useContext(NewDealJourneyContext);
  const [companySelectData, setCompanySelectData] = useState<SelectData>([]);
  const [projectsSelectData, setProjectsSelectData] = useState<SelectData>([]);
  const [showAddEditCompanyModal, setShowAddEditCompanyModal] = useState(false);
  const [pendingCompanyName, setPendingCompanyName] = useState("");
  // Mirrors what is typed into the picker, so the option list can be assembled against the same term.
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [showProjectCreatePanel, setShowProjectCreatePanel] = useState(false);

  const { associations, selectedInvestedInto, companyDraft, createdCompanyDrafts, projectDrafts, landholdingDrafts } =
    formValues;

  /*
    The deal's one company, whichever way it was set: a draft that exists only until the deal is saved, or a
    collaborator already chosen from the CRM. The picker needs a single thing to display.
  */
  const selectedCompany = useMemo(() => {
    if (companyDraft) return { key: companyDraft.key, name: companyDraft.name };

    const existing = associations.find((association) => association.objectType === "Collaborator");
    return existing ? { key: existing.objectUuid, name: existing.name } : null;
  }, [companyDraft, associations]);

  /*
    What the picker lists: the CRM search results, plus the companies created here — those exist only in the
    journey, so the server can never return them and they are matched against the search term locally.

    With an empty box the current company is pinned on as well, so the field can resolve its own display name
    (it clears itself when "Add new company" is chosen, and a company beyond the first page of results would
    otherwise be unnameable). While a search is running it is left out, so the list only shows real matches.
  */
  const companyPickerData = useMemo<SelectData>(() => {
    const term = companySearchTerm.trim().toLowerCase();
    const createdOptions = (createdCompanyDrafts ?? [])
      .filter((created) => !term || created.name.toLowerCase().includes(term))
      .map((created) => ({ key: created.key, value: created.name }));

    const options = [...createdOptions, ...companySelectData];

    if (term || !selectedCompany || options.some((option) => option.key === selectedCompany.key)) {
      return options;
    }

    return [{ key: selectedCompany.key, value: selectedCompany.name }, ...options];
  }, [selectedCompany, companySelectData, createdCompanyDrafts, companySearchTerm]);

  const filteredProjectsSelectData = useMemo<SelectData>(() => {
    const selectedKeys = new Set(
      associations
        .filter((association) => association.objectType === "Project" || association.objectType === "Landholding")
        .map((association) =>
          association.objectType === "Landholding"
            ? `${LANDHOLDING_OPTION_PREFIX}${association.objectUuid}`
            : association.objectUuid
        )
    );

    return projectsSelectData.filter((option) => !selectedKeys.has(String(option.key)));
  }, [associations, projectsSelectData]);

  const fetchCompanies = useCallback(async (searchTerm: string) => {
    const response = await searchCollaborators({
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 25,
      },
      sort: [{ key: "results.name", direction: "asc" }],
      filter: searchTerm
        ? {
            results: {
              name: {
                operator: "contains",
                value: searchTerm,
              },
            },
          }
        : undefined,
    });

    if (response.status === Status.Success && response.data?.results) {
      setCompanySelectData(response.data.results.map((company) => ({ key: company.uuid, value: company.name })));
      return;
    }

    setCompanySelectData([]);
  }, []);

  const fetchProjects = useCallback(async (searchTerm: string) => {
    const response = await searchProjects({
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 50,
      },
      sort: [{ key: "results.displayName", direction: "asc" }],
      filterGroups: getProjectSearchFilterGroups(searchTerm),
    } as SearchProjectsRequest & FilterGroups<SearchProjectsRequest["filter"]>);

    if (response.status === Status.Success && response.data?.results) {
      const options = new Map<string, SelectData[number]["value"]>();

      response.data.results.forEach((project) => {
        if (project.uuid) {
          options.set(project.uuid, project.displayName);
        }

        project.landholdings?.forEach((landholding) => {
          if (!landholding.groupUuid) return;
          const name = landholding.groupName;
          options.set(`${LANDHOLDING_OPTION_PREFIX}${landholding.groupUuid}`, name);
        });
      });

      setProjectsSelectData(
        Array.from(options.entries())
          .map(([key, value]) => ({ key, value }))
          .sort((left, right) => String(left.value ?? "").localeCompare(String(right.value ?? "")))
      );
      return;
    }

    setProjectsSelectData([]);
  }, []);

  useEffect(() => {
    fetchCompanies("");
    fetchProjects("");
  }, [fetchCompanies, fetchProjects]);

  const onCompanyTypeSearch = useCallback(
    (value: string) => {
      setCompanySearchTerm(value);
      fetchCompanies(value);
    },
    [fetchCompanies]
  );

  const onProjectTypeSearch = useCallback(
    (value: string) => {
      fetchProjects(value);
    },
    [fetchProjects]
  );

  const onInvestedIntoSelect = useCallback(
    (value: string) => {
      handleFieldChange("selectedInvestedInto")(value || null);
    },
    [handleFieldChange]
  );

  const onCompanySelect = (uuid: string): void => {
    if (uuid === "") return;
    // Already the deal's company, so re-picking it changes nothing.
    if (uuid === companyDraft?.key) return;

    // Picking a company created earlier in this journey selects that draft again rather than a CRM record.
    const created = (createdCompanyDrafts ?? []).find((draft) => draft.key === uuid);
    if (created) {
      setFormValues((prev) => replaceCompany(prev, { draft: created }));
      return;
    }

    // Resolved from the picker's options rather than the raw results, so every listed key yields a name.
    const name = companyPickerData.find((company) => company.key === uuid)?.value as string;

    setFormValues((prev) =>
      replaceCompany(prev, { association: { objectUuid: uuid, objectType: "Collaborator", name } })
    );
  };

  const onProjectSelect = (uuid: string): void => {
    if (uuid === "") return;

    const isLandholding = uuid.startsWith(LANDHOLDING_OPTION_PREFIX);
    const objectUuid = isLandholding ? uuid.replace(LANDHOLDING_OPTION_PREFIX, "") : uuid;
    const objectType = isLandholding ? "Landholding" : "Project";
    const name = projectsSelectData.find((x) => x.key === uuid)?.value as string;

    setFormValues((prev) => ({
      ...prev,
      associations: [
        ...prev.associations,
        {
          objectUuid,
          objectType,
          name,
        },
      ],
    }));
  };

  const removeAssociation = (association: DealAssociation): void => {
    setFormValues((prev) => ({
      ...prev,
      associations: prev.associations.filter(
        (value) => !(value.objectUuid === association.objectUuid && value.objectType === association.objectType)
      ),
    }));
  };

  // Carries the picker's in-progress search term into the form, so a name typed to search is not retyped.
  const openAddEditCompanyModal = useCallback((currentSearchTerm: string) => {
    setPendingCompanyName(currentSearchTerm);
    /*
      The term has done its job now that it has been handed to the form, and the company about to be created
      need not match it — the name is editable in the modal. Holding on to it would filter the new draft out of
      the option list below, leaving the picker unable to name the key it is being given.
    */
    setCompanySearchTerm("");
    setShowAddEditCompanyModal(true);
  }, []);

  const closeAddEditCompanyModal = useCallback(() => {
    setShowAddEditCompanyModal(false);
    setPendingCompanyName("");
  }, []);

  const openProjectCreatePanel = useCallback(() => {
    setShowProjectCreatePanel(true);
  }, []);

  const closeProjectCreatePanel = useCallback(() => {
    setShowProjectCreatePanel(false);
  }, []);

  const saveCompanyDraft = useCallback(
    (draft: CompanyDraft) => {
      /*
        Keyed here rather than trusting the form's own key: that one is generated once when the modal module
        loads, so every draft from a single page load would otherwise share it and collapse into one option.
      */
      // As when the modal opened: the picker has to be able to list the draft it is about to be pointed at.
      setCompanySearchTerm("");
      setFormValues((prev) => replaceCompany(prev, { draft: { ...draft, key: uuidv4() } }));
    },
    [setFormValues]
  );

  const saveProjects = useCallback(
    (projects: ProjectDraft[], landholding: LandholdingDraft, isLandholding: boolean) => {
      setFormValues((prev) => ({
        ...prev,
        projectDrafts: [
          ...prev.projectDrafts,
          ...projects.map((p) => ({ ...p, landholdingName: isLandholding ? landholding.name : undefined })),
        ],
        landholdingDrafts: isLandholding ? [...prev.landholdingDrafts, landholding] : prev.landholdingDrafts,
      }));
    },
    [setFormValues]
  );

  const clearCompanyDraft = useCallback(() => {
    setFormValues((prev) => ({
      ...prev,
      companyDraft: null,
    }));
  }, [setFormValues]);

  const removeLandholdingDraft = useCallback(
    (draftKey: string) => {
      setFormValues((prev) => {
        const landholding = prev.landholdingDrafts.find((draft) => draft.key === draftKey);
        return {
          ...prev,
          landholdingDrafts: prev.landholdingDrafts.filter((draft) => draft.key !== draftKey),
          projectDrafts: landholding
            ? prev.projectDrafts.filter((draft) => !landholding.projectKeys.includes(draft.key))
            : prev.projectDrafts,
        };
      });
    },
    [setFormValues]
  );

  const removeProjectDraft = useCallback(
    async (draftKey: string): Promise<void> => {
      const projectDraft = formValues.projectDrafts.find((draft) => draft.key === draftKey);
      const associationKey = projectDraft?.projectUuid ?? draftKey;

      if (projectDraft?.projectUuid) {
        const deleteProjectResponse = await deleteProject({ projectUuid: projectDraft.projectUuid });

        if (deleteProjectResponse.status === Status.Error) {
          Toast.error({
            message: deleteProjectResponse.errors?.[0]?.message ?? "Failed to delete project.",
          });
          return;
        }
      }

      setFormValues((prev) => ({
        ...prev,
        projectDrafts: prev.projectDrafts.filter((draft) => draft.key !== draftKey),
        landholdingDrafts: prev.landholdingDrafts.map((draft) => ({
          ...draft,
          projectKeys: draft.projectKeys.filter((x) => x !== draftKey),
        })),
        structureInvestments: prev.structureInvestments.map((investment) => ({
          ...investment,
          associations: investment.associations.filter((value) => value !== associationKey),
        })),
      }));
    },
    [formValues.projectDrafts, setFormValues]
  );

  return {
    errors,
    investingIntoSelectData,
    companySelectData,
    companyPickerData,
    selectedCompany,
    projectsSelectData: filteredProjectsSelectData,
    selectedInvestedInto,
    associations,
    companyDraft,
    projectDrafts,
    landholdingDrafts,
    showAddEditCompanyModal,
    pendingCompanyName,
    showProjectCreatePanel,
    openAddEditCompanyModal,
    closeAddEditCompanyModal,
    openProjectCreatePanel,
    closeProjectCreatePanel,
    saveCompanyDraft,
    saveProjects,
    clearCompanyDraft,
    removeLandholdingDraft,
    removeProjectDraft,
    onInvestedIntoSelect,
    onCompanySelect,
    onProjectSelect,
    onCompanyTypeSearch,
    onProjectTypeSearch,
    removeAssociation,
  };
};
